import { useEffect, useState } from 'react'
import {
  Box,
  Card,
  Chip,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded'
import SlideshowRoundedIcon from '@mui/icons-material/SlideshowRounded'
import PlayCircleRoundedIcon from '@mui/icons-material/PlayCircleRounded'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import { eventService } from '../services/eventService'
import { track } from '../services/telemetry'
import type { ContentItem, ContentKind } from '../data/types'
import { groupBy } from '../utils/format'
import PageContainer from '../components/ui/PageContainer'
import { LoadingState } from '../components/ui/States'

const KIND: Record<
  ContentKind,
  { icon: typeof PictureAsPdfRoundedIcon; color: string; label: string }
> = {
  pdf: { icon: PictureAsPdfRoundedIcon, color: '#C4621B', label: 'PDF' },
  slides: { icon: SlideshowRoundedIcon, color: '#0075BC', label: 'Slides' },
  video: { icon: PlayCircleRoundedIcon, color: '#3E9BA1', label: 'Video' },
  link: { icon: LinkRoundedIcon, color: '#1C5580', label: 'Link' },
}

export default function ContentPage() {
  const [items, setItems] = useState<ContentItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    eventService.getContent().then((c) => {
      setItems(c)
      setLoading(false)
    })
  }, [])

  if (loading) return <LoadingState />

  const groups = groupBy(items, (i) => i.category)

  return (
    <PageContainer
      title="Content Hub"
      subtitle="Briefings, presentations, and resources — all in one place"
    >
      <Stack spacing={4}>
        {[...groups.entries()].map(([category, group]) => (
          <Box key={category}>
            <Typography variant="overline" color="text.secondary" fontWeight={700}>
              {category}
            </Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {group.map((item) => {
                const meta = KIND[item.kind]
                const Icon = meta.icon
                const external = item.kind === 'link'
                return (
                  <Card
                    key={item.id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      borderRadius: 3,
                      '&:hover': { borderColor: 'primary.main' },
                    }}
                  >
                    <Box
                      sx={{
                        width: 46,
                        height: 46,
                        borderRadius: 2,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: `${meta.color}1A`,
                        color: meta.color,
                      }}
                    >
                      <Icon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle1" fontWeight={700} noWrap>
                          {item.title}
                        </Typography>
                        <Chip
                          label={meta.label}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            color: meta.color,
                            bgcolor: `${meta.color}1A`,
                          }}
                        />
                      </Stack>
                      {item.description && (
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {item.description}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {item.availableFrom ?? item.sizeLabel ?? ''}
                      </Typography>
                    </Box>
                    <IconButton
                      color="primary"
                      aria-label={external ? 'Open' : 'Download'}
                      onClick={() => {
                        track(external ? 'content_view' : 'content_download', {
                          entityType: 'content',
                          entityId: item.id,
                          metadata: { title: item.title },
                        })
                        if (item.url) window.open(item.url, '_blank', 'noopener')
                      }}
                    >
                      {external ? <OpenInNewRoundedIcon /> : <DownloadRoundedIcon />}
                    </IconButton>
                  </Card>
                )
              })}
            </Stack>
          </Box>
        ))}
      </Stack>
    </PageContainer>
  )
}
